import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Battery, Shield, Zap, ChevronRight, Maximize2 } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { getProductsByCategory, getBrand, getCategory } from '../../data/products';

const BatteriesCategoryPage = () => {
  const category = getCategory('batteries');
  const items = getProductsByCategory('batteries');

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://supaenergyhub.com.au/' },
        { '@type': 'ListItem', position: 2, name: 'Batteries', item: 'https://supaenergyhub.com.au/products/batteries' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://supaenergyhub.com.au/products/batteries/${p.slug}`,
        name: p.name,
      })),
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Home Battery Storage — Modular LFP Systems"
        description="Browse modular home battery systems available through Supa Energy Hub. High-voltage LFP batteries with 100% usable energy, IP65 outdoor protection and scalable capacity for Australian homes."
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Breadcrumb + Hero */}
        <section className="pt-32 pb-12 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="category-breadcrumb">
              <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#3D5A47] font-medium">Batteries</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Category · {category.tagline}
              </span>
              <h1 className="heading-1 mb-4 text-[#3D5A47]">
                Home Battery <span className="text-[#B5D43B]">Storage</span>
              </h1>
              <p className="body-large text-gray-600">{category.description}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 max-w-3xl">
              {[
                { icon: Battery, label: '100% usable kWh', sub: 'LFP depth-of-discharge' },
                { icon: Maximize2, label: 'Modular & scalable', sub: 'From 4.7 to 97 kWh' },
                { icon: Shield, label: 'IP65 outdoor-rated', sub: 'Flexible install zones' },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-2">
                    <s.icon className="w-5 h-5 text-[#3D5A47]" />
                  </div>
                  <p className="font-semibold text-sm text-[#3D5A47]">{s.label}</p>
                  <p className="text-xs text-gray-500">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <h2 className="heading-2 text-[#3D5A47]">Available Models</h2>
                <p className="text-gray-600 mt-1">Featured battery products from our trusted manufacturers.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((p) => {
                const brand = getBrand(p.brand);
                return (
                  <Link
                    key={p.slug}
                    to={`/products/batteries/${p.slug}`}
                    className="group relative bg-white rounded-2xl border border-gray-200 hover:border-[#B5D43B]/60 hover:shadow-xl transition-all overflow-hidden flex"
                    data-testid={`product-card-${p.slug}`}
                  >
                    {/* Left: product image with energy badge */}
                    <div className={`w-40 sm:w-48 flex-shrink-0 relative flex items-center justify-center p-3 ${p.imageBlendMode === 'normal' ? 'bg-gradient-to-br from-[#f6f8f3] via-white to-[#e9efe2]' : 'bg-gradient-to-br from-[#3D5A47] to-[#1a2820]'}`}>
                      <div className={`absolute top-3 left-3 z-10 bg-[#B5D43B] ${p.imageBlendMode === 'normal' ? 'text-[#1a2820]' : 'text-[#1a2820]'} rounded-lg px-2 py-1.5 text-center shadow-md`}>
                        <span className="block text-[7px] uppercase tracking-widest font-bold leading-none">From</span>
                        <div className="flex items-baseline justify-center leading-none mt-0.5">
                          <span className="text-xl font-black">{p.headlineSpec.value}</span>
                          <span className="text-xs font-black">{p.headlineSpec.unit}</span>
                        </div>
                      </div>
                      {p.image && (
                        <img
                          src={p.image}
                          alt={p.name}
                          className="h-36 w-auto object-contain"
                          style={p.imageBlendMode === 'normal' ? {} : { mixBlendMode: 'multiply' }}
                          loading="lazy"
                        />
                      )}
                      <span className={`absolute bottom-2 left-0 right-0 text-center text-[8px] uppercase tracking-widest font-mono ${p.imageBlendMode === 'normal' ? 'text-[#3D5A47]/30' : 'text-white/40'}`}>
                        {p.series}
                      </span>
                    </div>

                    {/* Right: Info */}
                    <div className="p-5 flex flex-col flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <img src={brand.logo} alt={brand.name} className="h-5 w-auto" />
                        <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                          {brand.name}
                        </span>
                      </div>
                      <h3 className="font-bold text-lg text-[#3D5A47] group-hover:text-[#B5D43B] transition-colors leading-tight">
                        {p.name.split(' (')[0]}
                      </h3>
                      <p className="text-xs text-gray-400 mb-2 font-mono">Model {p.model}</p>
                      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 flex-1">
                        {p.tagline}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3D5A47] bg-[#B5D43B]/15 px-2 py-1 rounded-full">
                          {p.heroPills?.[0]?.label || 'View'}
                        </span>
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-[#3D5A47] group-hover:text-[#B5D43B]">
                          View Specs <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Need help sizing your battery?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Our team will recommend the right battery size and brand based on your solar generation, household usage and applicable battery rebates.*
            </p>
            <Link to="/get-estimate">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2" data-testid="category-cta-estimate">
                Get a Free Estimate <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <div className="mt-6">
              <Link to="/brands" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" /> Browse all brands
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BatteriesCategoryPage;
